import React, { ReactNode, memo } from 'react';
import { getPlatformStyles } from '@direct-frontend/platform-code';

import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { TextPresetWithSkin } from '../TextGeneric/types';
import { HStack } from '../Stack';
import { SkinSize } from '../../lib/skin/getSizeSkin/generated/types';
import { HelpProps } from '../Help/types';
import { cn } from '../../lib/css/className/className';

getPlatformStyles({
    common: import('./InfoBase.css'),
});

const cls = cn('InfoBaseText');

export interface InfoBaseTextProps {
    /**
     * Основной текст (заголовок или абзац)
     */
    children?: ReactNode;

    /**
     * Дополнительный поясняющий текст (описание к заголовку)
     */
    caption?: ReactNode;

    /**
     * Компонент со всплывающей подсказкой при клике на знак вопроса
     */
    help?: (helpProps: HelpProps) => ReactNode;

    /**
     * Блок с меткой (отображается справа от текста)
     */
    labelBlock?: ReactNode;

    /**
     * Показывать ли labelBlock внутри текстового блока
     */
    showLabelInTextBlock?: boolean;

    /**
     * Размер скина для компонента
     */
    skinSize: SkinSize;

    /**
     * Размер текста (опционален, вычисляется автоматически если не передан)
     */
    textSize?: TextPresetWithSkin;

    /**
     * CSS класс для стилизации
     */
    className?: string;

    /**
     * Тип компонента (для определения расположения labelBlock)
     */
    type?: 'text' | 'icon' | 'illustration' | 'image' | 'custom';
}

/**
 * Компонент для отображения текстового блока в информационных компонентах.
 * Поддерживает заголовок, описание, help-подсказку и labelBlock.
 */
export const InfoBaseText = memo<InfoBaseTextProps>(function InfoBaseText({
    children,
    caption,
    help,
    labelBlock,
    showLabelInTextBlock,
    skinSize,
    textSize,
    className,
}) {
    const isLabelInTextBlock = labelBlock && showLabelInTextBlock;
    const finalTextSize: TextPresetWithSkin = textSize || (caption ? 'current-main + current-body' : 'current-main');

    return (
        <TextGenericWithSkin
            skinSize={skinSize}
            className={cls(null, [className])}
            textSize={finalTextSize}
            caption={caption}
            color="current"
            help={help}
            bold={Boolean(caption)}
        >
            {isLabelInTextBlock ? (
                <HStack justifyContent="space-between">
                    {children}
                    {labelBlock}
                </HStack>
            ) : (
                children
            )}
        </TextGenericWithSkin>
    );
});
